<template>
  <form
    class="login-form-phone"
    :aria-disabled="isLoading"
    @submit.prevent="onSubmit"
  >
    <div class="login-form-phone__field form-field">
      <el-input
        ref="codeInput"
        v-model="code"
        type="text"
        placeholder="Введите код"
        autocomplete="one-time-code"
        @input="clearError"
      />
      <span v-if="errors.code" class="error">{{ errors.code }}</span>
    </div>

    <el-button
      class="login-form-phone__submit-btn"
      type="primary"
      :loading="isLoading"
      :disabled="isLoading"
      native-type="submit"
    >
      Проверить
    </el-button>

    <div class="login-form-phone__caption">
      <p>
        Мы&nbsp;отправили код подтверждения в&nbsp;СМС на&nbsp;номер
        {{ formattedPhone }}.
        <br />
        Код действителен в течение 5 минут.
      </p>
      <el-button
        v-if="canResend"
        class="login-form-phone__retry-btn"
        type="secondary"
        :disabled="isLoading || isRetrying"
        @click="onRetry"
      >
        Отправить код повторно
      </el-button>
      <p v-else>Получить новый код можно через {{ countdownLabel }}</p>
    </div>

    <el-button
      class="login-form-phone__back-btn"
      type="text"
      :disabled="isLoading"
      @click="onBack"
    >
      Вернуться назад
    </el-button>
  </form>
</template>

<script>
import { useCountdown } from "@/composables/useCountdown";
import { formatPhoneNumber, sanitizePhoneNumber } from "@/utils/phone";

export default {
  name: "LoginFormPhoneVerification",
  props: {
    payload: {
      type: Object,
      required: true,
    },
    isRetrying: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const countdown = useCountdown();
    return {
      countdown,
      code: "",
      errors: {},
      authError: "",
      isLoading: false,
    };
  },
  computed: {
    formattedPhone() {
      const raw =
        this.payload && this.payload.msisdn ? String(this.payload.msisdn) : "";
      if (!raw) return "";
      let sanitized = sanitizePhoneNumber(raw);
      if (!sanitized.startsWith("+")) {
        sanitized = `+${sanitized}`;
      }
      if (sanitized.startsWith("+8")) {
        sanitized = `+7${sanitized.slice(2)}`;
      }
      if (/^\+7\d{10}$/.test(sanitized)) {
        return formatPhoneNumber(sanitized);
      }
      return sanitized;
    },
    countdownLabel() {
      return this.countdown.timer.value;
    },
    canResend() {
      return this.countdown.duration.value === -1;
    },
  },
  watch: {
    payload: {
      immediate: true,
      handler() {
        this.code = "";
        this.errors = {};
        this.authError = "";
        this.countdown.startTimer(5);
        this.focusCodeInput();
      },
    },
  },
  mounted() {
    this.focusCodeInput();
  },
  beforeDestroy() {
    this.countdown.stopTimer();
  },
  methods: {
    focusCodeInput() {
      this.$nextTick(() => {
        const input = this.$refs.codeInput;
        if (input && typeof input.focus === "function") {
          input.focus();
        }
      });
    },
    validate() {
      this.errors = {};
      const value = String(this.code || "").trim();
      if (!value) {
        this.errors.code = "Поле обязательно для заполнения";
      } else if (!/^\d+$/.test(value)) {
        this.errors.code = "Код должен содержать только цифры";
      } else if (value.length < 4 || value.length > 8) {
        this.errors.code = "Код должен содержать от 4 до 8 символов";
      }
      return Object.keys(this.errors).length === 0;
    },
    clearError() {
      if (this.errors.code) {
        this.errors = {};
      }
      if (this.authError) {
        this.authError = "";
      }
    },
    onBack() {
      this.$emit("back");
    },
    onRetry() {
      this.$emit("retry");
    },
    async onSubmit() {
      this.authError = "";
      if (!this.validate()) return;

      try {
        this.isLoading = true;
        await this.$store.dispatch("user/login", {
          code: this.code,
          msisdn: `+${this.payload.msisdn}`,
          sessionId: this.payload.sessionId,
          grant_type: "SMS",
        });
        this.$emit("success");
      } catch (error) {
        const responseData =
          error && error.response ? error.response.data : null;
        this.authError =
          (responseData &&
            (responseData.error_description || responseData.message)) ||
          "Произошла ошибка при отправке кода";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.login-form-phone {
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  overflow: hidden;
  background-color: #fff;
  border-radius: 4px;
  padding: 60px 60px 40px;
}

.login-form-phone__field {
  margin-bottom: 16px;
}

.login-form-phone__submit-btn {
  width: 100%;
  margin-bottom: 12px;
}

.login-form-phone__retry-btn {
  width: 100%;
  margin: 0;
}

.login-form-phone__back-btn {
  width: 100%;
  margin: 0;
}

.login-form-phone__caption {
  font-size: 14px;
  color: #606266;
}

.error {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #f56c6c;
}
</style>
